import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { client } from "./utils/api";
import { storeSelector } from "./modules/store/selectors";

const responseCache = new Map();

export const useFetch = (
  path,
  { cache = false, keepPrevious = false } = {}
) => {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(() => ({
    path,
    response: cache ? responseCache.get(path) : undefined,
    loading: !(cache && responseCache.has(path)),
  }));
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    async function doFetch() {
      if (cache && responseCache.has(path) && attempt === 0) {
        setResult({ path, response: responseCache.get(path), loading: false });
        return;
      }
      setResult((previous) => ({
        path,
        response: keepPrevious ? previous.response : undefined,
        loading: true,
      }));
      try {
        const response = await client(path, { signal });
        if (response === null || response === undefined)
          throw new Error("Empty response");
        if (!signal.aborted) {
          if (cache) responseCache.set(path, response);
          setResult({ path, response, loading: false });
        }
      } catch (error) {
        if (!signal.aborted) setResult({ path, error, loading: false });
      }
    }

    doFetch();

    return () => abortController.abort();
  }, [path, attempt, cache, keepPrevious]);

  const current =
    result.path === path
      ? result
      : cache && responseCache.has(path)
      ? { response: responseCache.get(path), loading: false }
      : { response: keepPrevious ? result.response : undefined, loading: true };
  return { ...current, retry };
};

const acknowledgedNoteKey = "eatally:acknowledged-note";

export const useNote = () => {
  const { note } = useSelector(storeSelector);
  const [acknowledgedNote, setAcknowledgedNote] = useState(() => {
    try {
      return window.localStorage.getItem(acknowledgedNoteKey);
    } catch {
      return null;
    }
  });

  const acknowledge = () => {
    setAcknowledgedNote(note);
    try {
      window.localStorage.setItem(acknowledgedNoteKey, note);
    } catch {
      // The note can still be dismissed when storage is unavailable.
    }
  };

  return { note: note ?? "", isSeen: note === acknowledgedNote, acknowledge };
};
